import React, { useEffect, useState } from 'react';
import PropTypes from "prop-types";
import { Link } from 'react-router-dom';
import Select from 'react-select';
import moment from 'moment-timezone';
import ReactTable from 'react-table';
import QueryString from 'query-string';
import { Popover, OverlayTrigger } from 'react-bootstrap';

import { GroupingsService, TagsService } from '../../services';
import PageTitle from '../PageTitle/page_title';
import { MultiRowSelection } from '../MultiRowSelection';
import {DatePickerInput} from '../DatePickerInput';
import i18next from "i18next";

import config from "config";

const REPORT_DATE_RANGE_DAY_VALUE = 0;
const REPORT_DATE_RANGE_WEEK_VALUE = 1;
const REPORT_DATE_RANGE_MONTH_VALUE = 2;

class EventSummaryReport extends React.Component {

  constructor(props) {
    super(props);

    const passedState = this.props.location.state ? this.props.location.state : window.state;

    if (passedState) {
      this.state = {
        reportConfig: {
          selectedGrouping: passedState.selectedGrouping,
          dateRange: passedState.dateRange,
          startDate: passedState.startDate,
          fromPrint: QueryString.parse(this.props.location.search)['print'] ? true : false,
          startAtDate: passedState.startAtDate,
          endAtDate: passedState.endAtDate,
          allAvailableAreaTags: [],
          resultAvailableAreaTags: [],
          entityNameFilter: "",
          summary: passedState.summary
        },
        filteredData: passedState.filteredData,
        counts: passedState.counts,
      }
    } else {
      this.state = {
        reportConfig: {
          selectedGrouping: null,
          dateRange: REPORT_DATE_RANGE_DAY_VALUE,
          startDate: null,
          fromPrint: false,
          startAtDate: null,
          endAtDate: null,
          allAvailableAreaTags: [],
          resultAvailableAreaTags: [],
          entityNameFilter: "",
          summary: false
        },
        filteredData: null,
        counts: { totalWarn: 0, totalInfo: 0, totalCrit: 0 }
      }
    }
  }

  componentDidMount() {
    if (this.state.reportConfig.fromPrint && !this.state.printing) {
      window.print();
      this.setState({printing: true})
    } else {
      TagsService.listAll().then(response => {
        this.setState({
          reportConfig: {
            ...this.state.reportConfig,
            allAvailableAreaTags: response.tags.filter(tag => tag.tagType.token === 'area').map(tag => ({id: tag.id, name: tag.name}))
          }
        });
      }).catch(error => {
        console.error(error);
      });
    }
  }

  calculateEndDate(start, dateRange) {
    if (dateRange === REPORT_DATE_RANGE_DAY_VALUE) {
      return moment(start).add(1, 'day')
    } else if (dateRange === REPORT_DATE_RANGE_WEEK_VALUE) {
      return moment(start).add(1, 'week')
    } else if (dateRange === REPORT_DATE_RANGE_MONTH_VALUE) {
      return moment(start).add(1, "month");
    } else {
      return moment(start).add(1, "day");
    }
  }

  refresh(reportConfig) {
    if (reportConfig.selectedGrouping != null) {
      const start = moment(reportConfig.startDate).startOf("day");
      const end = this.calculateEndDate(start, reportConfig.dateRange);

      this.setState({
        counts: { totalWarn: 0, totalInfo: 0, totalCrit: 0 }, 
        reportConfig: {...reportConfig, startAtDate: start.valueOf(), endAtDate: end.valueOf(), areaFilterOptions: [], entityNameFilter: ""}, 
        loading: true 
      }, () => {
          const groupingId = this.state.reportConfig.selectedGrouping.value;
          const groupId = this.state.reportConfig.selectedGrouping.grouping.deviceGroup.id;
          const startAtDate = this.state.reportConfig.startAtDate;
          const endAtDate = this.state.reportConfig.endAtDate;
          const summary = this.state.reportConfig.summary;
          GroupingsService.getEventReport(groupId, groupingId, startAtDate, endAtDate, summary)
                  .then((response) => {
                    if (summary) {
                      this.setState({ data: response.report.entries });
                    } else {
                      let counts = { totalWarn: 0, totalInfo: 0, totalCrit: 0 };
                      let reportValues = [];
                      let reportAreaTagIdSet = new Set();
                      response.report.eventReportDeviceGroupings.forEach((dgEntry) => {
                        dgEntry.eventSummaryReportEntries.forEach((reportEntry) => {
                          counts.totalInfo += reportEntry.informationalCount;
                          counts.totalWarn += reportEntry.warningCount;
                          counts.totalCrit += reportEntry.criticalCount;

                          reportEntry.areaTagIds.forEach((areaTagId) => reportAreaTagIdSet.add(areaTagId));

                          reportValues.push({
                            deviceGroupingId: dgEntry.deviceGroupingId,
                            deviceGroupingName: dgEntry.deviceGroupingName,
                            ...reportEntry
                          });
                        });
                      });
                      this.setState({
                        reportConfig: {
                          ...this.state.reportConfig,
                          resultAvailableAreaTags: this.state.reportConfig.allAvailableAreaTags.filter(aat => reportAreaTagIdSet.has(aat.id)).map(aat => ({...aat, selected: true}))
                        },
                        data: reportValues, 
                        filteredData: reportValues, 
                        counts: counts 
                      });
                    }
                  })
                  .catch((error) => {
                    console.error(error);
                    this.setState({ errorMessage: i18next.t("messages.could-not-load-devices") });
                  });
      });      
    }
  }

  reportConfigChanged(reportConfig) {
    if (this.state.reportConfig.selectedGrouping && 
        this.state.reportConfig.selectedGrouping?.value === reportConfig.selectedGrouping?.value &&
        this.state.reportConfig.startDate === reportConfig.startDate &&
        this.state.reportConfig.dateRange === reportConfig.dateRange &&
        this.state.reportConfig.summary === reportConfig.summary) {

      const filteredData = this.applyDataFiltering(reportConfig.entityNameFilter, reportConfig.resultAvailableAreaTags, this.state.data);

      let counts = { totalWarn: 0, totalInfo: 0, totalCrit: 0 };
      filteredData.forEach((reportEntry) => {
        counts.totalInfo += reportEntry.informationalCount;
        counts.totalWarn += reportEntry.warningCount;
        counts.totalCrit += reportEntry.criticalCount;
      });

      this.setState({ 
        reportConfig: reportConfig,
        filteredData: filteredData,
        counts: counts
      });

    } else {

      this.setState({ 
        reportConfig: reportConfig,
        filteredData: null,
        data: null,
        loading: false
      });

    }
  }

  applyDataFiltering(entityNameFilter, resultAvailableAreaTags, data) {
    if (data) {
      if (entityNameFilter || resultAvailableAreaTags) {
        const areaTagFilterIds = resultAvailableAreaTags.filter(aat => aat.selected).map(aat => aat.id);
        return data.filter(row => entityNameFilter ? row.entity.name.toLowerCase().includes(entityNameFilter.toLowerCase()) : true)
                   .filter(row => row.areaTagIds.length === 0 || row.areaTagIds.some(id => areaTagFilterIds.includes(id)));
      } else {
        return data;
      }
    } else {
      return [];
    }
  }

  render() {
    return (
      <div className = "device-detail-report event-summary-report">
        <div className="row">
          <PageTitle title={ i18next.t('pages.event-summary-report.header') } />
        </div>

        <EventSummaryReportConfigurationArea
          reportConfig={this.state.reportConfig}
          filteredData={this.state.filteredData}
          counts={this.state.counts}
          reportConfigChanged={this.reportConfigChanged.bind(this)}
          runReport={this.refresh.bind(this)}
          onError={(errorMessage) => this.setState({ errorMessage: errorMessage })}
        />
        
        { 
          this.state.filteredData || this.state.data ? (
              this.state.reportConfig.summary ?
              <EventSummaryReportResultsArea 
                reportConfig={this.state.reportConfig}
                data={this.state.data}
              /> 
              :
              <EventDetailReportResultsArea 
                reportConfig={this.state.reportConfig}
                filteredData={this.state.filteredData}
                counts={this.state.counts}
                reportConfigChanged={this.reportConfigChanged.bind(this)}
              />
            )
            : 
            this.state.loading && <div className="loader">{ i18next.t('loading') }</div>
        }
      </div>
    )
  }
}

const EventSummaryReportResultsArea = ({reportConfig, data}) => {
  let columns = [];

  console.log(reportConfig.selectedGrouping)

  if (reportConfig.selectedGrouping && reportConfig.selectedGrouping.value == -1) {
    columns.push({
      Header: reportConfig.selectedGrouping.grouping?.deviceGroup?.name ?? "",
      accessor: "levelName",
      sortable: true,
      minResizeWidth: 50,
      width: 150
    });
  }

  columns.push({
    Header: "Event",
    accessor: "eventName",
    minResizeWidth: 200,
    style:{'whiteSpace': 'unset'},
  }, {
    Header: "Event Level",
    columns: [{
      Header: i18next.t('table-headers.info'),
      accessor: "infoCount",
      id: "info",
      width: 90,
      minResizeWidth: 90,
      sortable: true
    }, {
      Header: i18next.t('table-headers.warn') ,
      accessor: "warningCount",
      id: "warning",
      width: 90,
      minResizeWidth: 90,
      sortable: true
    }, {
      Header: i18next.t('table-headers.crit'),
      accessor: "criticalCount",
      id: "critical",
      width: 90,
      minResizeWidth: 90,
      sortable: true
    }]
  });

  return (
    <div className="op-content-box">
      <ul className="list-inline clearfix">
        <li className="pull-left box-label"><h4 className="remove-margin-bottom">{ i18next.t('results') }</h4></li>
      </ul>
            
      <h3>Event Summary</h3>

      <div className="react-table-custom">
      {
        reportConfig.fromPrint ? 
        <ReactTable className="-striped -highlight" data={data} pageSize={data.length} columns={columns} /> 
        :
        <ReactTable className="-striped -highlight" data={data} pageSize={data.length} columns={columns} />
      }
      </div>
    </div>
  );
};

EventSummaryReportResultsArea.propTypes = {
  reportConfig: PropTypes.object.isRequired,
  data: PropTypes.array.isRequired
};


const EventDetailReportResultsArea = ({reportConfig, filteredData, counts, reportConfigChanged}) => {
  const [exportLoading, setExportLoading] = useState(false);

  const renderDisplayName = (deviceGroupingId, id, name, entityType, entitySerialNumber) => {
    const linkToEntityEvents = {
      pathname: `/monitoring/locations/${deviceGroupingId}/events`,
      search: entityType == 'asset' ? `?filterByDeviceIds=&filterByAssetIds=${id}` : `?filterByDeviceIds=${id}&filterByAssetIds=`,
      state: {
        fromPath: '/reporting/event-summary-report',
        selectedGrouping: reportConfig.selectedGrouping,
        dateRange: reportConfig.dateRange,
        startDate: moment(reportConfig.startAtDate).format('L')
    }}
    return <div title={entitySerialNumber}><Link to={linkToEntityEvents}>{name}</Link></div>;
  };

  const exportData = () => {
    setExportLoading(true);

    const groupingId = reportConfig.selectedGrouping.value;
    const groupId = reportConfig.selectedGrouping.grouping.deviceGroup.id;
    const startAtDate = reportConfig.startAtDate;
    const endAtDate = reportConfig.endAtDate;
    const summary = reportConfig.summary;
    GroupingsService.getAlertCountCSV(groupId, groupingId, startAtDate, endAtDate, summary)
        .then((_) => {
            setExportLoading(false);
        })
        .catch((error) => {
            console.error(error);
            setExportLoading(false);
        });
  }

  const updateAreaFilterValue = (updatedOptions) => {
    reportConfigChanged({
      ...reportConfig,
      resultAvailableAreaTags: updatedOptions
    });
  };

  const updateSearchValue = (inputValue) => {
    reportConfigChanged({
      ...reportConfig,
      entityNameFilter: inputValue.target.value
    });
  }

  const areaPopover = (
      <Popover className="filter-map-cat-selection filter-map-popover" id="area-selection-popover" title={ i18next.t('area') }>
        <MultiRowSelection options={reportConfig.resultAvailableAreaTags} onApplyFilters={updateAreaFilterValue} />
      </Popover>
  );

  let columns = [];

  if (reportConfig.selectedGrouping && reportConfig.selectedGrouping.value == -1) {
    columns.push({
      Header: i18next.t('table-headers.device-grouping'),
      accessor: "deviceGroupingName",
      sortable: true,
      minResizeWidth: 50,
      width: 150
    });
  }

  columns.push({
      Header: i18next.t('table-headers.asset'),
      accessor: "name",
      sortable: true,
      Cell: row => (renderDisplayName(row.original.deviceGroupingId, row.original.entity.id, row.original.entity.name, row.original.entity.entityType, row.original.entity.serialNumber)),
      minResizeWidth: 50,
      width: 200,
      filterable: !reportConfig.fromPrint,
      Filter: 
      <div>
        <input
          style={{width: "100%", margin:"0 auto"}}
          value={reportConfig.entityNameFilter}
          onChange={updateSearchValue}
          type="text"
          name="search"
          placeholder={ i18next.t('form-labels.search-by-name') }
          className="table-search"
          autoComplete="off"
        />
      </div>
    }, {
      Header: i18next.t('table-headers.events'),
      accessor: "eventsTriggered",
      minResizeWidth: 200,
      Cell: row => row.original.eventsTriggered.join(", "),
      style:{'whiteSpace': 'unset'},
    }, {
      Header: i18next.t('table-headers.area'),
      accessor: "tags",
      minResizeWidth: 100,
      width: 200,
      Cell: row => <span>{row.original.combinedAreaTagNames}</span>,
      sortable: true,
      filterable: !reportConfig.fromPrint,
      Filter: _ => 
      <div>
        <OverlayTrigger trigger="click" rootClose placement="bottom" overlay={ areaPopover }>
          <a className="table-filter-button area-filter">{ i18next.t('form-labels.filter') }</a>
        </OverlayTrigger>
      </div>
    }, {
      Header: "Event Level",
        columns: [
          {
            Header: i18next.t('table-headers.info'),
            accessor: "informationalCount",
            id: "info",
            width: 90,
            minResizeWidth: 90,
            filterable: true,
            Filter: _ => <div className = "total"> ({counts.totalInfo}) </div>
          },
          {
            Header: i18next.t('table-headers.warn') ,
            accessor: "warningCount",
            id: "warning",
            width: 90,
            minResizeWidth: 90,
            sortable: true,
            filterable: true,
            Filter: _ => <div className = "total"> ({counts.totalWarn}) </div>
          },
          {
            Header: i18next.t('table-headers.crit'),
            accessor: "criticalCount",
            id: "critical",
            width: 90,
            minResizeWidth: 90,
            sortable: true,
            filterable: true,
            Filter: _ => <div className = "total"> ({counts.totalCrit}) </div>
          }
        ]
    });
  
  return (
    <div className="op-content-box">
      <ul className="list-inline clearfix">
        <li className="pull-left box-label"><h4 className="remove-margin-bottom">{ i18next.t('results') }</h4></li>
      </ul>
            
      <h3>Asset Event Details</h3>

      <div className="react-table-custom">
      {
        reportConfig.fromPrint ? 
        <ReactTable className="-striped -highlight" data={filteredData} pageSize={filteredData.length} columns={columns} /> 
        :
        <ReactTable
          className="-striped -highlight"
          data={filteredData}
          exportData={exportData}
          exportLoading={exportLoading}
          showPaginationTop
          columns={columns}
        />
      }
      </div>
    </div>
  );
};

EventDetailReportResultsArea.propTypes = {
  reportConfig: PropTypes.object.isRequired,
  filteredData: PropTypes.array.isRequired,
  counts: PropTypes.object.isRequired
};

const EventSummaryReportConfigurationArea = ({reportConfig, filteredData, counts, reportConfigChanged, runReport, onError}) => {
  const [availableGroupValues, setAvailableGroupValues] = useState(null);
  const [availableGroupingValues, setAvailableGroupingValues] = useState(null);
  const [allGroupings, setAllGroupings] = useState(null);
  const [selectedGroup, setSelectedGroup] = useState(reportConfig.selectedGrouping?.grouping?.deviceGroup ? {value: reportConfig.selectedGrouping.grouping.deviceGroup.id, label: reportConfig.selectedGrouping.grouping.deviceGroup.name, group: reportConfig.selectedGrouping.grouping.deviceGroup} : null);

  useEffect(() => {
    if (!reportConfig.fromPrint) {
      GroupingsService.listAll().then((response) => {
        setAllGroupings(response.groupings);

        const deviceGroups = Object.values(response.groupings.reduce((map, grouping) => {
            map[grouping.deviceGroup.id] = grouping.deviceGroup;
            return map;
          }, {}))
          .sort((a, b) => a.displayOrder > b.displayOrder)
          .map(group => ({value: group.id, label: group.name, group: group}));

        setAvailableGroupValues(deviceGroups);
        if (reportConfig.selectedGrouping?.grouping) {
          setSelectedGroup(deviceGroups.find(deviceGroup => deviceGroup.value == reportConfig.selectedGrouping.grouping.deviceGroup?.id));
        } else {
          setSelectedGroup(deviceGroups[deviceGroups.length - 1]);
        }
      }).catch((error) => {
          console.error(error);
          onError(i18next.t('messages.could-not-load-groupings'));
          setAvailableGroupingValues([])
      });
    }
  }, []);

  useEffect(() => {
    if (selectedGroup != null && allGroupings != null && !reportConfig.fromPrint) {
      let groupings = allGroupings
              .filter(grouping => (grouping.deviceGroup.id == selectedGroup.value))
              .sort((a, b) => a.name.localeCompare(b.name))
              .map(grouping => ({ value: grouping.id, label: grouping.name, grouping: grouping }));

      if (selectedGroup.group.displayOrder > 1) {
        groupings.unshift({ value: -1, label: "All", grouping: { id: -1, deviceGroup: selectedGroup.group } });
      }

      reportConfigChanged({
          ...reportConfig,
          selectedGrouping: groupings.length == 1 ? groupings[0] : null
      });
      setAvailableGroupingValues(groupings);
    }
  }, [selectedGroup, allGroupings]);

  const printClicked = () => {
    let printWindow = window.open(`${window.location.href}?print=true`, '_blank') //to open new page
    printWindow.state = {
      filteredData: filteredData,
      counts: counts,
      selectedGrouping: reportConfig.selectedGrouping,
      dateRange: reportConfig.dateRange,
      startDate: moment(reportConfig.startDate).format('L'),
      startAtDate: reportConfig.startAtDate,
      endAtDate: reportConfig.endAtDate,
      summary: reportConfig.summary
    }
  };

  const onSubmit = () => {
    runReport(reportConfig);
  };

  const updateSelectedGroupingValue = (selectedGrouping) => {
    reportConfigChanged({
      ...reportConfig,
      selectedGrouping: selectedGrouping
    });
  };

  const updateDateRangeValue = (dateRange) => {
    reportConfigChanged({
      ...reportConfig,
      dateRange: dateRange
    });
  };

  const updateStartDateValue = (startDate) => {
    reportConfigChanged({
      ...reportConfig,
      startDate: startDate
    });
  }; 

  const updateSummary = (summary) => {
    reportConfigChanged({
      ...reportConfig,
      summary: summary
    });
  }; 

  const DATE_RANGE_OPTIONS = [
      { value: REPORT_DATE_RANGE_DAY_VALUE, label: i18next.t("form-labels.day") },
      { value: REPORT_DATE_RANGE_WEEK_VALUE, label: i18next.t("form-labels.week") },
      { value: REPORT_DATE_RANGE_MONTH_VALUE, label: i18next.t("form-labels.month") }
  ];

  return (
      <div className="op-content-box filter-box" style={{ marginBottom: 20 }}>
          <ul className="list-inline clearfix">
              <li className="pull-left box-label">
                  <h4 className="remove-margin-bottom">{i18next.t("filters")}</h4>
              </li>
          </ul>

          <div className="row">
              <div className="col-sm-3">
                  <label>{i18next.t("form-labels.timeframe")}</label>
                  <Select
                      options={DATE_RANGE_OPTIONS}
                      clearable={false}
                      value={DATE_RANGE_OPTIONS[reportConfig.dateRange]}
                      onChange={(rangeSelection) => updateDateRangeValue(rangeSelection.value)}
                      searchable={false}
                      classNamePrefix="react-select"
                      placeholder={i18next.t("form-labels.select-timeframe")}
                      isDisabled={reportConfig.fromPrint}
                  />
              </div>

              <div className="pull-left" style={{ maxWidth: "150px", margin: "0px 0px 0px 10px" }}>
                  <label>{i18next.t("form-labels.start-date")}</label>
                  <DatePickerInput input={{ value: reportConfig.startDate }} meta={{}} isDisabled={reportConfig.fromPrint} onDayChange={updateStartDateValue} />
              </div>

              <div className="col-sm-3">
                  <label>Level</label>
                  <Select
                      options={availableGroupValues == null ? [] : availableGroupValues}
                      clearable={false}
                      value={selectedGroup}
                      onChange={setSelectedGroup}
                      searchable={true}
                      classNamePrefix="react-select"
                      placeholder="Select Level"
                      isDisabled={reportConfig.fromPrint}
                      isLoading={!reportConfig.fromPrint && availableGroupValues == null}
                  />
              </div>

              <div className="col-sm-3">
                  <label>{selectedGroup?.group?.name ?? <span>&nbsp;</span>}</label>
                  <Select
                      options={availableGroupingValues == null ? [] : availableGroupingValues}
                      clearable={false}
                      value={reportConfig.selectedGrouping}
                      onChange={updateSelectedGroupingValue}
                      searchable={true}
                      classNamePrefix="react-select"
                      placeholder={`Select ${selectedGroup?.group?.name}`}
                      isDisabled={availableGroupValues == null || reportConfig.fromPrint}
                      isLoading={!reportConfig.fromPrint && availableGroupingValues == null}
                  />
              </div>

              {!reportConfig.fromPrint && filteredData && (
                  <div className="pull-right left-print tooltip">
                      <a>
                          <img src={`${config.WEB_BASE_URL}/images/icon/themes/${window.globalBrand}/print_icon.svg`} className="print-icon" onClick={printClicked} alt={i18next.t("print-report")} />
                      </a>
                      <span className="tooltiptext left-print">{i18next.t("print")}</span>
                  </div>
              )}

              {!reportConfig.fromPrint && (
                  <div className="pull-left">
                      <label style={{ visibility: "hidden" }}>{i18next.t("form-labels.run")}</label>
                      <ul className="list-inline clearfix">
                          <li className="pull-left" style={{ marginLeft: "15px" }}>
                              <button type="submit" onClick={onSubmit} disabled={!(reportConfig.selectedGrouping && reportConfig.startDate)} className="btn btn-sm btn-default pull-right">
                                  {i18next.t("form-labels.run")}
                              </button>
                          </li>
                      </ul>
                  </div>
              )}
          </div>
          <div className="row">
              <div className="col-sm-3">
                  <label>Summarize</label>
                  Yes <input type="radio" checked={reportConfig.summary} onClick={() => updateSummary(true)} name="summarize" />
                  &nbsp;
                  No <input type="radio" checked={!reportConfig.summary} onClick={() => updateSummary(false)} name="summarize" />
              </div>
          </div>
      </div>
  );
};

EventSummaryReportConfigurationArea.propTypes = {
  reportConfig: PropTypes.object.isRequired,
  filteredData: PropTypes.array,
  counts: PropTypes.object.isRequired,
  reportConfigChanged: PropTypes.func.isRequired,
  runReport: PropTypes.func.isRequired,
  onError: PropTypes.func.isRequired
};

export default EventSummaryReport;
